"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Sparkles, Droplets } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function ThreeOfCupsPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-blue-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-blue-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-blue-500">/</span>
            <span className="text-sm">Minör Arkana</span>
            <span className="text-blue-500">/</span>
            <span className="text-sm">Kupalar</span>
            <span className="text-blue-500">/</span>
            <span className="text-sm">Üç Kupa</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Minör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/cups"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Kupalar
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Üç Kupa</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-blue-900/20 to-cyan-900/20 rounded-xl p-8 mb-8 border border-blue-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/rider-waite-three-of-cups.svg"
                  alt="Three of Cups Tarot Card"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-700 rounded-full flex items-center justify-center">
                  <Droplets className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">Three of Cups</h1>
                  <p className="text-xl text-blue-400">Üç Kupa / Kutlama ve Dostluk</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Arkadaşlık, kutlama ve toplumsal sevinç kartı. Grup halinde eğlence,
                başarıları paylaşma ve dostlarla geçirilen mutlu anları temsil eder.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Dostluk</span>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Kutlama</span>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Topluluk</span>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Neşe</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Güçlü Dostluklar</h3>
                  <p className="text-slate-400 text-sm">Sadık arkadaşlar, destek sistemi ve sosyal bağlar.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Başarı Kutlamaları</h3>
                  <p className="text-slate-400 text-sm">Ödüller, başarıları paylaşma ve grup halinde sevinç.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sosyal Etkinlikler</h3>
                  <p className="text-slate-400 text-sm">Partiler, düğünler ve keyifli buluşmalar.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yaratıcı İşbirliği</h3>
                  <p className="text-slate-400 text-sm">Grup projelerinde uyum ve üretkenlik.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sosyal Dışlanma</h3>
                  <p className="text-slate-400 text-sm">Grup dışında kalma, yalnızlık ve izolasyon.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Aşırı İçki ve Eğlence</h3>
                  <p className="text-slate-400 text-sm">Hedonizm, kontrolsüz davranışlar ve israf.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Dedikodu ve Çekişme</h3>
                  <p className="text-slate-400 text-sm">Grup içi kavgalar ve arkadan konuşma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yüzeysel İlişkiler</h3>
                  <p className="text-slate-400 text-sm">Samimiyetsiz arkadaşlıklar ve sahte dostluk.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Sembolik Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👭</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Üç Kadın</h3>
              <p className="text-slate-400 text-sm">Kadın enerjisi, sezgi ve duygusal bağ.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🥂</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kaldırılan Kupalar</h3>
              <p className="text-slate-400 text-sm">Kutlama, tebrik ve paylaşılan sevinç.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌺</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Çiçekler</h3>
              <p className="text-slate-400 text-sm">Bereket, güzellik ve doğal büyüme.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🍎</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Meyveler</h3>
              <p className="text-slate-400 text-sm">Bolluk, hasatın keyfini çıkarma.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">💃</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Dans Hareketi</h3>
              <p className="text-slate-400 text-sm">Neşe, ritim ve hayat sevinci.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-cyan-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">☀️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Parlak Hava</h3>
              <p className="text-slate-400 text-sm">Pozitif enerji ve aydınlık ruh hali.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Evlilik ve nişan kutlamaları</li>
                <li>• Aile büyümesi</li>
                <li>• Dostlar önünde mutluluk</li>
                <li>• Sosyal çevrede kabul</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Takım başarıları</li>
                <li>• İş yerinde dostluklar</li>
                <li>• Proje kutlamaları</li>
                <li>• Networking etkinlikleri</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sun className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Sosyal becerilerin gelişimi</li>
                <li>• Özgüven artışı</li>
                <li>• Yaratıcı ifade</li>
                <li>• Toplum içinde yer alma</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Droplets className="h-5 w-5 text-blue-400" />
                Sağlık ve Yaşam
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Moral yüksekliği</li>
                <li>• Sosyal destek sistemi</li>
                <li>• Stres azalması</li>
                <li>• Pozitif yaşam tarzı</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/cups/two-of-cups"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            İki Kupa
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/cups/four-of-cups"
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Dört Kupa
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}